/**
 * Smart Control Baseline Assumption Resolver
 *
 * Resolves whether the baseline should assume Smart Control is present,
 * based on user input and recommended options.
 *
 * Key logic:
 * - If user says "yes" -> baseline has smart control
 * - If user says "no" -> baseline does NOT have smart control
 * - If user says "unknown" (default):
 *   - If any option includes Smart Control -> baseline does NOT have it
 *   - Otherwise -> baseline does NOT have it (conservative assumption)
 */

import type { SystemRecInput, RecommendationOption, ComputeEvidence, SmartControlType } from './types';

/**
 * Determine if an option includes Smart Control
 * 
 * Smart Control is considered present if:
 * - Thermostat with smart_on_off or smart_load_comp control type
 * - Or other indicators (add-ons, control strategies)
 */
export function optionIncludesSmartControl(option: RecommendationOption): boolean {
  // Check if thermostat recommendation includes smart control
  if (option.thermostat) {
    const controlType = option.thermostat.recommendedType;
    if (controlType === 'smart_on_off' || controlType === 'smart_load_comp') {
      return true;
    }
  }

  // Future: Check for add-ons like "SMART_CONTROL" if that field exists
  // Future: Check for controlStrategy === "smart" if that field exists

  return false;
}

/**
 * Resolve whether baseline should assume Smart Control is present
 * 
 * @param input - User input
 * @param options - All recommended options (primary + alternatives + considered)
 * @param evidence - Evidence object to track assumptions (mutated)
 * @returns boolean - true if baseline assumes smart control, false otherwise
 */
export function resolveBaselineSmartControl(
  input: SystemRecInput,
  options: RecommendationOption[],
  evidence?: ComputeEvidence
): boolean {
  const hasSmartControl = input.hasSmartControl || 'unknown';

  // Explicit user input takes priority
  if (hasSmartControl === 'yes') {
    if (evidence) {
      evidence.appliedInputs['hasSmartControl'] = 'yes';
    }
    return true;
  }

  if (hasSmartControl === 'no') {
    if (evidence) {
      evidence.appliedInputs['hasSmartControl'] = 'no';
    }
    return false;
  }

  // hasSmartControl === 'unknown' (default)
  // Check if any recommended option includes Smart Control
  const anyOptionHasSmartControl = options.some(opt => optionIncludesSmartControl(opt));

  if (anyOptionHasSmartControl) {
    // Baseline assumes NO smart control when it's being recommended
    if (evidence) {
      evidence.fallbacksUsed.push({
        field: 'hasSmartControl',
        assumed: 'no',
        reason: 'Smart Control is included in recommended options; baseline assumes it is not currently installed so savings reflect the upgrade.',
        value: false,
      });
    }
    return false;
  }

  // No option includes smart control, so assume baseline doesn't have it either
  // (This is the conservative default - no smart control unless explicitly stated)
  if (evidence) {
    evidence.fallbacksUsed.push({
      field: 'hasSmartControl',
      assumed: 'no',
      reason: 'No explicit smart control information provided; baseline assumes no smart controls (conservative default).',
      value: false,
    });
  }
  return false;
}

/**
 * Apply smart control savings uplift to efficiency
 *
 * IMPORTANT: Only MODULATING smart controls (smart_load_comp) get savings uplift.
 *
 * smart_on_off controls do NOT receive uplift because:
 * - They cannot modulate boiler output (relay-only control)
 * - They lose ~50% efficiency benefit on load-compensation-capable boilers
 * - Studies show learning-only thermostats can increase energy use due to short-cycling
 * - Savings claims for on/off smart stats are disputed and context-dependent
 *
 * Only load-compensating controls with direct boiler communication
 * receive the full efficiency benefit.
 *
 * @param baseEfficiency - Base system efficiency (0-1 scale)
 * @param hasSmartControl - Whether smart control is present
 * @param smartControlType - Type of smart control ('smart_on_off' or 'smart_load_comp')
 * @returns Adjusted efficiency with smart control uplift
 */
export function applySmartControlUplift(
  baseEfficiency: number,
  hasSmartControl: boolean,
  smartControlType?: 'smart_on_off' | 'smart_load_comp'
): number {
  if (!hasSmartControl) {
    return baseEfficiency;
  }

  // ONLY smart_load_comp gets savings uplift
  // smart_on_off does NOT get uplift - too uncertain and disputed
  let upliftPct = 0;

  if (smartControlType === 'smart_load_comp') {
    // Load compensation: 15-22% fuel savings (use mid-range 18%)
    // This is well-evidenced when boiler can modulate output based on room temperature delta
    upliftPct = 18;
  }
  // smart_on_off: NO uplift (0%)
  // Default/unknown: NO uplift (0%)

  if (upliftPct === 0) {
    return baseEfficiency;
  }

  // Convert fuel savings % to efficiency uplift
  // If fuel savings are 18%, that means fuel consumption is reduced by 18%
  // So if consumption was 100 units, it's now 82 units for the same heat output
  // This means efficiency increases: new_eff = old_eff / (1 - upliftPct/100)
  const upliftFactor = 1 / (1 - upliftPct / 100);
  const newEfficiency = baseEfficiency * upliftFactor;

  // Cap at 100% efficiency (1.0) for gas boilers
  return Math.min(1.0, newEfficiency);
}

/**
 * Classify a thermostat control type into SmartControlType category
 *
 * IMPORTANT: smart_on_off is classified as BASIC_PROGRAMMER
 * because it does NOT provide the efficiency benefits of true load compensation.
 * Only smart_load_comp qualifies as COMPENSATING_MODERN.
 *
 * This is a deliberate de-risking decision:
 * - On/off smart thermostats are relay-only (no modulation)
 * - Studies show learning-only stats can increase energy use
 * - We don't want to over-promise savings that depend on user behavior
 *
 * @param controlType - ThermostatControlType from recommendation
 * @returns SmartControlType classification
 */
export function classifySmartControlType(
  controlType: 'mechanical' | 'basic_digital' | 'smart_on_off' | 'smart_load_comp' | undefined
): SmartControlType {
  if (!controlType || controlType === 'mechanical') {
    return 'NONE';
  }

  if (controlType === 'basic_digital') {
    return 'BASIC_PROGRAMMER';
  }

  if (controlType === 'smart_load_comp') {
    // TRUE modulating control with load compensation - this gets the savings
    return 'COMPENSATING_MODERN';
  }

  if (controlType === 'smart_on_off') {
    // Relay-only smart stats - treat as BASIC_PROGRAMMER
    // They have scheduling/app features but NO efficiency uplift
    // Do NOT classify as LEARNING_CLOUD which implies some savings potential
    return 'BASIC_PROGRAMMER';
  }

  return 'NONE';
}

/**
 * Determine if smart control uplift should be applied
 * Only apply uplift if baseline does not have effective modern controls
 * 
 * @param baselineSmartControlType - Classification of baseline smart control
 * @param optionSmartControlType - Classification of option smart control
 * @returns true if uplift should be applied
 */
export function shouldApplySmartControlUplift(
  baselineSmartControlType: SmartControlType,
  optionSmartControlType: SmartControlType
): boolean {
  // Only apply uplift if option provides COMPENSATING_MODERN and baseline doesn't have it
  if (optionSmartControlType === 'COMPENSATING_MODERN') {
    return baselineSmartControlType !== 'COMPENSATING_MODERN';
  }
  
  return false;
}

/**
 * Get evidence note for smart control assumption
 *
 * IMPORTANT: We only apply efficiency uplift for COMPENSATING_MODERN controls.
 * Relay-only smart stats are treated as BASIC_PROGRAMMER
 * and do NOT receive efficiency uplift.
 *
 * @param baselineSmartControlType - Classification of baseline smart control
 * @returns Evidence note string
 */
export function getSmartControlEvidenceNote(
  baselineSmartControlType: SmartControlType
): string {
  if (baselineSmartControlType === 'NONE') {
    return "Baseline assumes no effective smart control. Savings require load-compensating controls - relay-only smart stats do not qualify.";
  }

  if (baselineSmartControlType === 'BASIC_PROGRAMMER') {
    return "Baseline assumes basic or relay-only control. Efficiency savings only apply with load-compensating controls that modulate boiler output.";
  }

  if (baselineSmartControlType === 'LEARNING_CLOUD') {
    // This case should now be rare as smart_on_off maps to BASIC_PROGRAMMER
    return "Baseline assumes learning-style smart control without modulation. No efficiency uplift applied - only load-compensating controls qualify.";
  }

  if (baselineSmartControlType === 'UNSUPPORTED_LEGACY') {
    return "Baseline assumes legacy/unsupported smart control. Savings require modern load-compensating controls.";
  }

  return "Baseline has modern load-compensating control. No additional smart control savings applied.";
}
